import { ChangeEvent, useMemo, useState, useCallback, useEffect, useRef } from 'react';

import { Route, useNavigate, useParams } from 'react-router-dom';
import { Routes } from '@datadog/browser-rum-react/react-router-v6';

import cn from 'classnames';

import * as Dfns from 'date-fns/fp';
import { Workbook } from 'exceljs';
import * as uuid from 'uuid';

import { ListGroup, Tooltip, Dropdown, DropdownButton, OverlayTrigger } from 'react-bootstrap';

import { MdMenu } from 'react-icons/md';

import { Network, useNetworkMap } from '../../redux/networks';
import { useSetError } from '../../redux/modals';

import {
  awaitJSON,
  SEQUENTIAL_VISIBILITY_WAIT_TIMEOUT_MS,
  StreamingOrdersLambdaFetch,
  StreamingV2LambdaFetch
} from '../../utils/fetch-utils';
import { exportToExcel } from '../../utils/download-utils';
import {
  getLocalChangesForCompany,
  makeStrBuyingLocalStorageKey
} from '../../utils/streaming-buying-storage';
import { useStateFunction } from '../../utils/hooks/useData';
import { useLocationCompany } from '../../utils/hooks/useLocationCompany';
import { usePageTitleNav } from '../../utils/hooks/useNav';

import { Page } from '../../Components/Page';
import { PAGE_NAMES } from '../../Components/Page/page-constants';
import { EntityKind, EntityLogo, EntitySize } from '../../Components/EntityLogo';
import { FullPageSpinner } from '../../Components/Spinner';
import {
  Skeleton,
  FilterBarSkeleton,
  FILTER_BAR_SKELETON_HEIGHT,
  ListGroupSkeleton
} from '../../Components/Skeleton';
import { Button } from '../../Components/Button';
import { BreadcrumbTitle } from '../../Components/BreadcrumbTitle';
import { Appearance, Background, Design, Intent, Size } from '../../Components/designConstants';
import { FilterBar } from '../../Components/FilterBar';
import { DateBox } from '../../Components/DateBox';
import { InteractiveDiv } from '../../Components/InteractiveEl';

import { OrderView } from './OrderView';
import { ImportReviewModal } from './ImportReviewModal';
import {
  downloadFlightImportTemplate,
  TemplateType,
  TemplateTypeValue
} from './flightImportTemplate';
import {
  ImportLocalStorageUpdate,
  ImportReview,
  ImportReviewRow,
  NetworkOrderMap,
  Order,
  OrderInfo,
  ProblemCounts,
  StreamingNetworkInfo
} from './streaming-buying-types';
import {
  cellToImportText,
  getValidPlatforms,
  normalizePunctuation,
  toStatus
} from './streaming-buying-utils';
import { useOrderInfo, useTagInfo } from './use-streaming-buying';

import './StreamingBuying.scss';

const AUDIO_EXPORT_FIELDS = {
  'Buy Type': 'audioBuyType',
  'Show Title': 'showTitle',
  'Spot Position': 'spotPosition',
  'Spot Rate': 'spotRate',
  'Num of Spots': 'numOfSpots',
  'Episode Air Dates': 'episodeAirDates'
};

const ROUTES = [
  {
    key: 'network',
    label: '*',
    uri: '*',
    subRoutes: [
      {
        key: 'order',
        label: '*',
        uri: '*'
      }
    ]
  }
];

const FILTER_BAR_OPTIONS = [
  { name: 'shortCode', label: 'Short Code' },
  { name: 'name', label: 'Name' },
  { name: 'networkGroup', label: 'Network Group' }
];

type BuyingInfoMeta = ProblemCounts & { refetch: () => void };

const useBuyingInfo = (): [] | [NetworkOrderMap, BuyingInfoMeta] => {
  const company = useLocationCompany() ?? '';
  const setError = useSetError();

  const { data: orderInfo, error: orderInfoError, refetch } = useOrderInfo(company);
  const { data: tagInfo, error: tagInfoError } = useTagInfo(company);

  useEffect(() => {
    if (orderInfoError) {
      setError({
        message: `Failed to fetch order info. Error: ${orderInfoError.message}`,
        reportError: orderInfoError
      });
    }
  }, [orderInfoError, setError]);

  useEffect(() => {
    if (tagInfoError) {
      setError({
        message: `Failed to fetch tag info. Error: ${tagInfoError.message}`,
        reportError: tagInfoError
      });
    }
  }, [tagInfoError, setError]);

  return useMemo<[] | [NetworkOrderMap, BuyingInfoMeta]>(() => {
    if (!orderInfo || !tagInfo) {
      return [];
    }
    const networkOrderMap: NetworkOrderMap = {};
    const unsavedMap = getLocalChangesForCompany(company);
    let unsent = 0;
    let unconfirmed = 0;
    for (const orderLine of orderInfo) {
      const networkOrders = networkOrderMap[orderLine.network] || [];
      networkOrders.push(orderLine);
      networkOrderMap[orderLine.network] = networkOrders;
      if (orderLine.status === 0) {
        unsent++;
      } else if (orderLine.status < 20) {
        unconfirmed++;
      }
    }

    for (const key of Object.keys(networkOrderMap)) {
      networkOrderMap[key] = networkOrderMap[key].slice().sort((a, b) => {
        const startDiff = (b.start ?? '').localeCompare(a.start ?? '');
        if (startDiff) return startDiff;
        const endDiff = (b.end ?? '').localeCompare(a.end ?? '');
        if (endDiff) return endDiff;
        return a.id - b.id;
      });
    }

    let missingTags = 0;
    for (const tag of tagInfo) {
      if (!(tag.hasPlacement || tag.hasOverride)) {
        missingTags++;
      }
    }
    return [
      networkOrderMap,
      {
        unsaved: Object.keys(unsavedMap).length,
        unsent,
        unconfirmed,
        missingTags,
        refetch
      }
    ];
  }, [orderInfo, tagInfo, company, refetch]);
};

interface OrderListProps {
  orders: OrderInfo[];
  network: string;
}

const OrderList = ({ orders, network }: OrderListProps) => {
  const navigate = useNavigate();
  const company = useLocationCompany();
  if (orders.length) {
    return (
      <ListGroup>
        {orders.map(({ id, start, end, status, descriptions }) => {
          const detailsList = descriptions.map(details => details || '(none)').join(', ');
          return (
            <ListGroup.Item
              key={id}
              action
              data-testid={`order-row-${id}`}
              className='order-row'
              onClick={() => {
                navigate(`/${company}/streaming/buying/${network}/${id}`);
              }}
            >
              <div className='id'>{id}</div>
              <DateBox className='date' start={start} end={end} />
              <OverlayTrigger
                placement='top-start'
                delay={500}
                overlay={<Tooltip>{detailsList}</Tooltip>}
              >
                <div className='details-list'>{detailsList}</div>
              </OverlayTrigger>
              <div className='status'>
                {toStatus(status)} ({status})
              </div>
            </ListGroup.Item>
          );
        })}
      </ListGroup>
    );
  }
  return <div data-testid='no-orders'>No Orders</div>;
};

interface NetworkViewProps {
  network: string;
  orders: OrderInfo[];
}

const NetworkView = ({ network, orders }: NetworkViewProps) => {
  const navigate = useNavigate();
  const company = useLocationCompany();
  const networkMap = useNetworkMap();
  if (!networkMap) {
    return null;
  }
  const networkInfo = networkMap[network];
  if (!networkInfo) {
    // Network is not defined. If it isn't in all caps, try that first.
    if (network !== network.toUpperCase()) {
      navigate(`/${company}/streaming/buying/${network.toUpperCase()}`, { replace: true });
    } else {
      navigate(`/${company}/streaming/buying`, { replace: true });
    }
    return null;
  }
  const { color } = networkInfo;

  return (
    <div className='network-view' data-testid='network-view'>
      <div className='button-row'>
        <Button
          size={Size.LG}
          data-testid='create-order-button'
          onClick={() => {
            navigate(`/${company}/streaming/buying/${network}/new`);
          }}
        >
          Create Order
        </Button>
      </div>
      <div className='order-list'>
        <OrderList orders={orders} network={network} />
      </div>
      <div className='stripe' style={{ backgroundColor: color }} />
    </div>
  );
};

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Normalize a date value from various formats to YYYY-MM-DD.
 * Handles: YYYY-MM-DD, M/D/YY, M/D/YYYY, MM/DD/YYYY, MM-DD-YYYY.
 * Returns the normalized string or null if unparseable.
 */
const normalizeDate = (raw: string | undefined): string | null => {
  if (!raw) {
    return null;
  }

  const str = raw.trim();

  // Already YYYY-MM-DD
  if (DATE_REGEX.test(str)) {
    const parsed = new Date(`${str}T00:00:00`);
    if (!Number.isNaN(parsed.getTime())) {
      return str;
    }
  }

  // M/D/YY, M/D/YYYY, MM/DD/YY, MM/DD/YYYY (with / or - separator)
  const mdyMatch = str.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  if (mdyMatch) {
    const [, m, d, yRaw] = mdyMatch;
    let year = parseInt(yRaw, 10);
    if (year < 100) {
      year += year < 70 ? 2000 : 1900;
    }
    const month = m.padStart(2, '0');
    const day = d.padStart(2, '0');
    const candidate = `${year}-${month}-${day}`;
    const parsed = new Date(`${candidate}T00:00:00`);
    if (!Number.isNaN(parsed.getTime())) {
      return candidate;
    }
  }

  return null;
};

const REQUIRED_IMPORT_COLUMNS = ['Network', 'Platform', 'Start Date', 'End Date'];

interface NetworkListProps {
  setProblemCounts: (counts: ProblemCounts) => void;
}

const NetworkList = ({ setProblemCounts }: NetworkListProps) => {
  const navigate = useNavigate();
  const { '*': network = '' } = useParams();
  const company = useLocationCompany();
  type NetworkLineFilter = (line: Network) => boolean;
  const [filter, setFilterRaw] = useStateFunction<NetworkLineFilter>(() => true);
  const networkMap = useNetworkMap();
  const networks = useMemo(() => {
    if (networkMap) {
      return Object.values(networkMap)
        .filter(n => n.buyable && !n.parent)
        .sort((a, b) => a.shortCode.localeCompare(b.shortCode));
    }
    return undefined;
  }, [networkMap]);

  const filteredNetworks = useMemo(() => networks?.filter(filter) ?? [], [networks, filter]);

  const [networkOrderMap, buyingInfoMeta] = useBuyingInfo();
  const refetchBuyingInfo = buyingInfoMeta?.refetch;
  useEffect(() => {
    if (buyingInfoMeta) {
      const { refetch: _, ...counts } = buyingInfoMeta;
      setProblemCounts(counts);
    }
  }, [buyingInfoMeta, setProblemCounts]);

  const [bookedOnly, setBookedOnly] = useState(false);
  const [exporting, setExporting] = useState(false);
  const setError = useSetError();

  const handleExportActiveFlights = useCallback(async () => {
    if (!networkOrderMap || exporting) {
      return;
    }

    setExporting(true);
    try {
      const today = Dfns.format('yyyy-MM-dd', new Date());

      const allOrders = Object.values(networkOrderMap).flat();
      const activeOrders = allOrders.filter(order => order.end >= today);

      if (activeOrders.length === 0) {
        setExporting(false);
        return;
      }

      interface FetchedOrder {
        id: number;
        network: string;
        status: number;
        flights?: Record<string, unknown>[];
      }
      const orderDetails = await Promise.all(
        activeOrders.map(async orderInfo => {
          const res = await StreamingOrdersLambdaFetch('/order', {
            params: { id: orderInfo.id }
          });
          return awaitJSON<FetchedOrder>(res);
        })
      );

      const rows: Record<string, unknown>[] = [];
      for (const order of orderDetails) {
        const lsKey = makeStrBuyingLocalStorageKey(company ?? '', order.network, order.id);
        const lsVal = JSON.parse(window.localStorage.getItem(lsKey) || '{}') as {
          flightChangeMap?: Record<string, Record<string, unknown>>;
        };
        const flightChangeMap = lsVal.flightChangeMap || {};

        // Merge unsaved changes onto existing flights
        for (const flight of order.flights ?? []) {
          const flightId = flight.id as string | number;
          const flightChange = flightChangeMap[flightId] || {};
          const hasChanges = Object.keys(flightChange).length > 0;
          const merged: Record<string, unknown> = { ...flight, ...flightChange };
          if ((merged.endDate as string) >= today && !merged.canceled) {
            const isHistorical = Boolean(merged.useDescription);
            const row: Record<string, unknown> = {
              'Order ID': order.id,
              Network: order.network,
              Status: hasChanges ? 'Unsaved' : toStatus(order.status),
              'Flight ID': merged.id,
              'Start Date': merged.startDate,
              'End Date': merged.endDate,
              Platform: merged.platform,
              Description: isHistorical ? merged.description : '',
              Content: isHistorical ? '' : merged.content,
              Additional: isHistorical ? '' : merged.additional,
              Spend: merged.spend,
              CPM: merged.cpm,
              Impressions: merged.impressions,
              DSP: merged.dsp
            };
            for (const [header, field] of Object.entries(AUDIO_EXPORT_FIELDS)) {
              row[header] = merged[field] || '';
            }
            rows.push(row);
          }
        }

        // Include unsaved new flights
        for (const changes of Object.values(flightChangeMap)) {
          if (changes.isNew && (changes.endDate as string) >= today && !changes.canceled) {
            const isHistorical = Boolean(changes.useDescription);
            const row: Record<string, unknown> = {
              'Order ID': order.id,
              Network: order.network,
              Status: 'Unsaved',
              'Flight ID': '(new)',
              'Start Date': changes.startDate,
              'End Date': changes.endDate,
              Platform: changes.platform,
              Description: isHistorical ? changes.description : '',
              Content: isHistorical ? '' : changes.content,
              Additional: isHistorical ? '' : changes.additional,
              Spend: changes.spend,
              CPM: changes.cpm,
              Impressions: changes.impressions,
              DSP: changes.dsp
            };
            for (const [header, field] of Object.entries(AUDIO_EXPORT_FIELDS)) {
              row[header] = changes[field] || '';
            }
            rows.push(row);
          }
        }
      }

      if (rows.length > 0) {
        exportToExcel(rows, `Active_Flights_${company}_${today}`);
      }
    } catch (e) {
      setError({
        message: `Failed to export active flights. Error: ${e.message}`,
        reportError: e
      });
    } finally {
      setExporting(false);
    }
  }, [networkOrderMap, exporting, company, setError]);

  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  const handleDownloadTemplate = useCallback(
    async (templateType: TemplateTypeValue) => {
      if (!networkMap || downloadingTemplate) {
        return;
      }

      setDownloadingTemplate(true);
      try {
        const buyableNetworks = Object.values(networkMap)
          .filter(n => n.buyable && !n.parent)
          .sort((a, b) => a.shortCode.localeCompare(b.shortCode));

        const networkOptions: Record<string, Record<string, unknown>> = {};
        await Promise.all(
          buyableNetworks.map(async n => {
            try {
              const res = await StreamingV2LambdaFetch('/network_buying_info', {
                params: { network: n.shortCode }
              });
              networkOptions[n.shortCode] = await awaitJSON(res);
            } catch {
              // Skip networks where buying info is unavailable
            }
          })
        );

        const allNetworkCodes = buyableNetworks
          .filter(n => networkOptions[n.shortCode])
          .map(n => n.shortCode);

        await downloadFlightImportTemplate(
          networkOptions,
          networkMap,
          allNetworkCodes,
          templateType
        );
      } catch (e) {
        setError({
          message: `Failed to download template. Error: ${e.message}`,
          reportError: e
        });
      } finally {
        setDownloadingTemplate(false);
      }
    },
    [networkMap, downloadingTemplate, setError]
  );

  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [importReview, setImportReview] = useState<ImportReview | null>(null);

  const handleAcceptImportReview = useCallback(() => {
    if (!importReview) {
      return;
    }
    for (const [lsKey, lsVal] of Object.entries(importReview.localStorageUpdates)) {
      window.localStorage.setItem(lsKey, JSON.stringify(lsVal));
    }
    const count = importReview.rows.length;
    setError({
      title: 'Changes Accepted',
      message: `${count} flight${count !== 1 ? 's' : ''} marked as pending. Navigate to each order to review and save.`,
      intent: Intent.SUCCESS
    });
    setImportReview(null);
    refetchBuyingInfo?.();
  }, [importReview, setError, refetchBuyingInfo]);

  const handleDiscardImportReview = useCallback(() => {
    setImportReview(null);
  }, []);

  const handleImportFlights = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file || !networkOrderMap || !networkMap) {
        return;
      }

      setImporting(true);
      setImportProgress('Reading file...');
      try {
        const buffer = await file.arrayBuffer();
        const workbook = new Workbook();
        await workbook.xlsx.load(buffer);
        const worksheet = workbook.worksheets[0];

        const headerRow = worksheet.getRow(1);
        const headers: string[] = [];
        for (let c = 1; c <= worksheet.columnCount; c++) {
          headers.push(String(headerRow.getCell(c).value ?? '').trim());
        }

        const rows: Record<string, string>[] = [];
        for (let r = 2; r <= worksheet.rowCount; r++) {
          const excelRow = worksheet.getRow(r);
          const row: Record<string, string> = {};
          let hasAnyValue = false;
          for (let c = 0; c < headers.length; c++) {
            const header = headers[c];
            if (!header) {
              continue;
            }
            const text = cellToImportText(excelRow.getCell(c + 1));
            row[header] = text;
            if (text) {
              hasAnyValue = true;
            }
          }
          if (hasAnyValue) {
            rows.push(row);
          }
        }
        setImportProgress(`Validating ${rows.length} row${rows.length !== 1 ? 's' : ''}...`);

        if (rows.length === 0) {
          setError({ title: 'Import Error', message: 'The spreadsheet has no data rows.' });
          setImporting(false);
          return;
        }

        // Check required columns
        const columns = Object.keys(rows[0]);
        const missingColumns = REQUIRED_IMPORT_COLUMNS.filter(col => !columns.includes(col));
        if (missingColumns.length > 0) {
          setError({
            title: 'Import Error',
            message: `Missing required columns: ${missingColumns.join(', ')}`
          });
          setImporting(false);
          return;
        }

        const parseRow = (row: Record<string, string>) => {
          const orderIdRaw = String(row['Order ID'] || '').trim();
          const flightIdRaw = String(row['Flight ID'] || '').trim();
          return {
            networkCode: String(row.Network || '').trim(),
            orderIdRaw,
            orderId: /^\d+$/.test(orderIdRaw) ? parseInt(orderIdRaw, 10) : null,
            // A numeric Flight ID means this row targets an existing flight for budget updates only.
            // "(new)" or blank means a new flight (existing behavior).
            flightId: /^\d+$/.test(flightIdRaw) ? parseInt(flightIdRaw, 10) : null,
            platform: String(row.Platform || '').trim(),
            startDate: normalizeDate(row['Start Date']) || String(row['Start Date'] || '').trim(),
            endDate: normalizeDate(row['End Date']) || String(row['End Date'] || '').trim(),
            description: normalizePunctuation(String(row.Description || '')),
            content: String(row.Content || '').trim(),
            additional: normalizePunctuation(String(row.Additional || '')),
            spend: row.Spend,
            cpm: row.CPM,
            impressions: row.Impressions,
            dsp: String(row.DSP || '').trim(),
            audioBuyType: String(row['Buy Type'] || '').trim(),
            showTitle: normalizePunctuation(String(row['Show Title'] || '')),
            spotPosition: String(row['Spot Position'] || '').trim(),
            spotRate: row['Spot Rate'],
            numOfSpots: row['Num of Spots'],
            episodeAirDates: String(row['Episode Air Dates'] || '').trim()
          };
        };

        const errors: string[] = [];
        for (let i = 0; i < rows.length; i++) {
          const {
            networkCode,
            orderId,
            platform,
            startDate,
            endDate,
            spend,
            cpm,
            impressions,
            flightId
          } = parseRow(rows[i]);
          const rowNum = i + 2; // 1-indexed + header row
          const isUpdate = flightId !== null;

          if (!networkCode) {
            errors.push(`Row ${rowNum}: Network is required`);
          } else if (!networkMap[networkCode] || !networkMap[networkCode].buyable) {
            errors.push(`Row ${rowNum}: Invalid network "${networkCode}"`);
          } else if (isUpdate) {
            // Updates require a valid Order ID; flight existence is verified after fetch.
            if (orderId === null) {
              errors.push(`Row ${rowNum}: Order ID is required when Flight ID is provided`);
            } else {
              const networkOrders = networkOrderMap[networkCode] || [];
              if (!networkOrders.some(o => o.id === orderId)) {
                errors.push(
                  `Row ${rowNum}: Order ${orderId} does not exist for network "${networkCode}"`
                );
              }
            }
          } else {
            if (!platform) {
              errors.push(`Row ${rowNum}: Platform is required`);
            } else {
              const validPlatforms = getValidPlatforms(networkCode, networkMap);
              if (!validPlatforms.includes(platform)) {
                errors.push(
                  `Row ${rowNum}: Invalid platform "${platform}" for network "${networkCode}"`
                );
              }
            }

            if (orderId !== null) {
              const networkOrders = networkOrderMap[networkCode] || [];
              if (!networkOrders.some(o => o.id === orderId)) {
                errors.push(
                  `Row ${rowNum}: Order ${orderId} does not exist for network "${networkCode}"`
                );
              }
            }
          }

          if (!isUpdate) {
            if (!startDate || !DATE_REGEX.test(startDate)) {
              errors.push(
                `Row ${rowNum}: Invalid Start Date (expected a date like 2026-04-01 or 4/1/26)`
              );
            }
            if (!endDate || !DATE_REGEX.test(endDate)) {
              errors.push(
                `Row ${rowNum}: Invalid End Date (expected a date like 2026-04-01 or 4/1/26)`
              );
            }
            if (DATE_REGEX.test(startDate) && DATE_REGEX.test(endDate) && endDate < startDate) {
              errors.push(`Row ${rowNum}: End Date is before Start Date`);
            }
          }

          if (spend != null && spend !== '' && (Number.isNaN(Number(spend)) || Number(spend) < 0)) {
            errors.push(`Row ${rowNum}: Spend must be a non-negative number`);
          }
          if (cpm != null && cpm !== '' && (Number.isNaN(Number(cpm)) || Number(cpm) < 0)) {
            errors.push(`Row ${rowNum}: CPM must be a non-negative number`);
          }
          if (
            impressions != null &&
            impressions !== '' &&
            (Number.isNaN(Number(impressions)) || Number(impressions) < 0)
          ) {
            errors.push(`Row ${rowNum}: Impressions must be a non-negative number`);
          }
        }

        if (errors.length > 0) {
          setError({
            title: 'Import Validation Errors',
            message: errors.join('\n')
          });
          setImporting(false);
          return;
        }

        // Split rows into budget updates (have Flight ID) and new flights.
        type ParsedImportRow = ReturnType<typeof parseRow>;
        const updateRows: ParsedImportRow[] = [];
        const newFlightRows: ParsedImportRow[] = [];
        for (const row of rows) {
          const parsed = parseRow(row);
          if (parsed.flightId !== null) {
            updateRows.push(parsed);
          } else {
            newFlightRows.push(parsed);
          }
        }

        // --- Process new flights (existing behavior) ---
        const existingOrderFlights: Record<string, Record<string, unknown>[]> = {}; // { "network:orderId": [flights] }
        const newOrderFlights: Record<string, Record<string, unknown>[]> = {}; // { "network:newKey": [flights] }

        for (const parsed of newFlightRows) {
          const { networkCode, orderIdRaw } = parsed;
          const networkInfo = networkMap[networkCode] as StreamingNetworkInfo | undefined;
          const enableStreamingBuyingUpdates =
            networkInfo?.enableTaxonomyUpdates || company === 'blisspoint';
          const useDescription = Boolean(parsed.description && !parsed.content);
          const flight: Record<string, unknown> = {
            platform: parsed.platform,
            startDate: parsed.startDate,
            endDate: parsed.endDate,
            description: parsed.description,
            content: parsed.content,
            additional: parsed.additional,
            spend: parsed.spend != null && parsed.spend !== '' ? Number(parsed.spend) : 0,
            cpm: parsed.cpm != null && parsed.cpm !== '' ? Number(parsed.cpm) : 0,
            impressions:
              parsed.spend != null &&
              parsed.spend !== '' &&
              parsed.cpm != null &&
              parsed.cpm !== '' &&
              Number(parsed.cpm) > 0
                ? Math.round((Number(parsed.spend) / Number(parsed.cpm)) * 1000)
                : 0,
            dsp: parsed.dsp,
            subFlights: [],
            audioBuyType: parsed.audioBuyType,
            showTitle: parsed.showTitle,
            spotPosition: parsed.spotPosition,
            spotRate:
              parsed.spotRate != null && parsed.spotRate !== '' ? Number(parsed.spotRate) : '',
            numOfSpots:
              parsed.numOfSpots != null && parsed.numOfSpots !== ''
                ? Number(parsed.numOfSpots)
                : '',
            episodeAirDates: parsed.episodeAirDates,
            enableStreamingBuyingUpdates: enableStreamingBuyingUpdates && !useDescription,
            useDescription
          };

          if (/^\d+$/.test(orderIdRaw)) {
            const key = `${networkCode}:${orderIdRaw}`;
            if (!existingOrderFlights[key]) {
              existingOrderFlights[key] = [];
            }
            existingOrderFlights[key].push(flight);
          } else {
            const key = `${networkCode}:${orderIdRaw}`;
            if (!newOrderFlights[key]) {
              newOrderFlights[key] = [];
            }
            newOrderFlights[key].push(flight);
          }
        }

        let importedCount = 0;
        const existingOrderCount = Object.keys(existingOrderFlights).length;
        const newOrderCount = Object.keys(newOrderFlights).length;

        if (existingOrderCount > 0) {
          setImportProgress(
            `Adding flights to ${existingOrderCount} existing order${existingOrderCount !== 1 ? 's' : ''}...`
          );
        }
        for (const [key, flights] of Object.entries(existingOrderFlights)) {
          const [networkCode, orderIdStr] = key.split(':');
          const orderId = parseInt(orderIdStr, 10);
          const lsKey = makeStrBuyingLocalStorageKey(company ?? '', networkCode, orderId);
          const lsVal = JSON.parse(window.localStorage.getItem(lsKey) || '{}') as {
            flightChangeMap?: Record<string, unknown>;
          };
          const flightChangeMap: Record<string, unknown> = lsVal.flightChangeMap || {};

          for (const flight of flights) {
            const flightId = uuid.v4();
            flightChangeMap[flightId] = { ...flight, id: flightId, isNew: true };
            importedCount++;
          }

          window.localStorage.setItem(lsKey, JSON.stringify({ ...lsVal, flightChangeMap }));
        }

        let newOrderIndex = 0;
        for (const [key, flights] of Object.entries(newOrderFlights)) {
          newOrderIndex++;
          setImportProgress(`Creating new order ${newOrderIndex} of ${newOrderCount}...`);
          const [networkCode] = key.split(':');
          // Sequential import loop — one order's request must complete before the next starts, so
          // this needs the short visibility-wait bound (see SEQUENTIAL_VISIBILITY_WAIT_TIMEOUT_MS's
          // comment) rather than the long default.
          await StreamingOrdersLambdaFetch('/orders', {
            method: 'PUT',
            body: {
              network: networkCode,
              company,
              status: 0,
              commit: false,
              pdfText: '',
              flights,
              isGhostBidding: false
            },
            visibilityWaitTimeoutMs: SEQUENTIAL_VISIBILITY_WAIT_TIMEOUT_MS
          });
          importedCount += flights.length;
        }

        if (newOrderCount > 0) {
          refetchBuyingInfo?.();
        }

        // --- Process budget updates (rows with a Flight ID) ---
        if (updateRows.length > 0) {
          setImportProgress(
            `Loading data for ${updateRows.length} flight update${updateRows.length !== 1 ? 's' : ''}...`
          );

          const uniqueOrderIds = [
            ...new Set(updateRows.map(r => r.orderId).filter((id): id is number => id != null))
          ];

          const orderDataMap: Record<number, Order> = {};
          for (const orderId of uniqueOrderIds) {
            // Sequential lookup loop — see the short-bound comment on the /orders call above.
            const res = await StreamingOrdersLambdaFetch('/order', {
              params: { id: orderId },
              visibilityWaitTimeoutMs: SEQUENTIAL_VISIBILITY_WAIT_TIMEOUT_MS
            });
            orderDataMap[orderId] = await awaitJSON<Order>(res);
          }

          const reviewRows: ImportReviewRow[] = [];
          const localStorageUpdates: Record<string, ImportLocalStorageUpdate> = {};

          for (const update of updateRows) {
            const { orderId } = update;
            const order = orderId == null ? undefined : orderDataMap[orderId];
            if (!order || orderId == null || update.flightId == null) {
              continue;
            }

            const flight = (order.flights || []).find(f => f.id === update.flightId);
            if (!flight) {
              setError({
                title: 'Import Error',
                message: `Flight ID ${update.flightId} not found in Order ${orderId}. Re-export and try again.`
              });
              return;
            }

            const newSpend =
              update.spend != null && update.spend !== '' ? Number(update.spend) : flight.spend;
            const newCpm =
              update.cpm != null && update.cpm !== '' ? Number(update.cpm) : flight.cpm;
            const computedImpressions =
              newSpend > 0 && newCpm > 0 ? Math.round((newSpend / newCpm) * 1000) : 0;
            // Only honor an explicit impressions value if it differs from the current flight value.
            // When the user re-uploads an exported sheet without changing impressions, the old value
            // is still present in the column — treating it as an override would prevent recalculation.
            const explicitImpressions =
              update.impressions != null && update.impressions !== ''
                ? Number(update.impressions)
                : null;
            let newImpressions: number;
            if (explicitImpressions !== null && explicitImpressions !== flight.impressions) {
              newImpressions = explicitImpressions;
            } else if (newSpend === 0) {
              newImpressions = 0;
            } else {
              newImpressions = computedImpressions || flight.impressions;
            }
            const isCancellation = newSpend === 0;

            // Skip rows with no effective change.
            if (
              newSpend === flight.spend &&
              newCpm === flight.cpm &&
              newImpressions === flight.impressions &&
              !isCancellation
            ) {
              continue;
            }

            reviewRows.push({
              orderId,
              network: order.network,
              flightId: update.flightId,
              platform: flight.platform,
              startDate: flight.startDate,
              endDate: flight.endDate,
              oldSpend: flight.spend,
              newSpend,
              oldCpm: flight.cpm,
              newCpm,
              oldImpressions: flight.impressions,
              newImpressions,
              isCancellation
            });

            const lsKey = makeStrBuyingLocalStorageKey(company ?? '', order.network, orderId);
            if (!localStorageUpdates[lsKey]) {
              const currentLsVal = JSON.parse(window.localStorage.getItem(lsKey) || '{}');
              localStorageUpdates[lsKey] = {
                ...currentLsVal,
                flightChangeMap: { ...(currentLsVal.flightChangeMap || {}) }
              };
            }
            localStorageUpdates[lsKey].flightChangeMap[update.flightId] = {
              ...(localStorageUpdates[lsKey].flightChangeMap[update.flightId] || {}),
              spend: newSpend,
              cpm: newCpm,
              impressions: newImpressions,
              canceled: isCancellation
            };
          }

          if (reviewRows.length > 0) {
            setImportReview({
              rows: reviewRows,
              localStorageUpdates,
              newFlightsImported: importedCount
            });
            // Feedback for updates comes from the review modal; skip the success toast below.
            return;
          }
        }

        // Show success toast for new-flight-only imports.
        if (importedCount > 0) {
          setError({
            title: 'Import Successful',
            message: `Imported ${importedCount} flight${importedCount !== 1 ? 's' : ''}.${
              existingOrderCount > 0
                ? ' Flights for existing orders are unsaved — navigate to the order to review and save.'
                : ''
            }${
              newOrderCount > 0
                ? ` Created ${newOrderCount} new order${newOrderCount !== 1 ? 's' : ''}.`
                : ''
            }`,
            intent: Intent.SUCCESS
          });
        }
      } catch (importError) {
        setError({
          message: `Failed to import flights. Error: ${importError.message}`,
          reportError: importError
        });
      } finally {
        setImporting(false);
        setImportProgress('');
      }
    },
    [networkOrderMap, networkMap, company, setError, refetchBuyingInfo]
  );

  return networkOrderMap ? (
    <>
      <div className='filter-container' data-testid='network-filter-container'>
        <Button
          size={Size.LG}
          data-testid='booked-only-button'
          appearance={bookedOnly ? Appearance.FILLED : Appearance.OUTLINED}
          onClick={() => setBookedOnly(v => !v)}
        >
          Booked Only
        </Button>
        <FilterBar
          size={Size.LG}
          options={FILTER_BAR_OPTIONS}
          onFilter={setFilterRaw}
          lines={networks ?? []}
        />
        {(exporting || importing || downloadingTemplate) && (
          <span className='menu-progress'>
            {exporting && 'Exporting...'}
            {importing && (importProgress || 'Importing...')}
            {downloadingTemplate && 'Preparing template...'}
          </span>
        )}
        <DropdownButton variant='outline-primary' title={<MdMenu />} align='end'>
          <Dropdown.Item onClick={handleExportActiveFlights} disabled={exporting}>
            {exporting ? 'Exporting...' : 'Export Active Flights'}
          </Dropdown.Item>
          <Dropdown.Item onClick={() => fileInputRef.current?.click()} disabled={importing}>
            {importing ? 'Importing...' : 'Import Flights'}
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Header>Import Templates</Dropdown.Header>
          <Dropdown.Item
            onClick={() => handleDownloadTemplate(TemplateType.VIDEO)}
            disabled={downloadingTemplate}
          >
            Video (Streaming)
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => handleDownloadTemplate(TemplateType.AUDIO)}
            disabled={downloadingTemplate}
          >
            Audio
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => handleDownloadTemplate(TemplateType.DISPLAY)}
            disabled={downloadingTemplate}
          >
            Display
          </Dropdown.Item>
        </DropdownButton>
        <input
          type='file'
          accept='.xlsx'
          ref={fileInputRef}
          className='hidden-file-input'
          onChange={handleImportFlights}
        />
      </div>
      <div className='network-list-container'>
        <div className='network-list' data-testid='network-list'>
          {filteredNetworks
            .filter(({ shortCode }) => !bookedOnly || networkOrderMap[shortCode])
            .map(({ shortCode, color, name }) => (
              <InteractiveDiv
                key={shortCode}
                data-testid={`network-row-${shortCode}`}
                style={{ borderColor: color }}
                className={cn('network-row', { expanded: shortCode === network })}
                onClick={() => {
                  navigate(
                    network === shortCode
                      ? `/${company}/streaming/buying`
                      : `/${company}/streaming/buying/${shortCode}`
                  );
                }}
              >
                <div className='logo'>
                  <EntityLogo
                    kind={EntityKind.NETWORK}
                    id={shortCode}
                    name={name}
                    size={EntitySize.XL}
                  />
                </div>
                <div className='name'>
                  {name} <span className='short-code'>({shortCode})</span>
                </div>
              </InteractiveDiv>
            ))}
        </div>
        {network && <NetworkView network={network} orders={networkOrderMap[network] || []} />}
      </div>
      <ImportReviewModal
        review={importReview}
        onAccept={handleAcceptImportReview}
        onDiscard={handleDiscardImportReview}
      />
    </>
  ) : (
    <Skeleton>
      {/* TODO: might be nice to detect if network is selected (we know from URL) and show right
          pane skeleton as well */}
      <FilterBarSkeleton />
      <ListGroupSkeleton
        y={FILTER_BAR_SKELETON_HEIGHT + 24}
        lineHeight={72}
        height={h => h - (FILTER_BAR_SKELETON_HEIGHT + 24)}
      />
    </Skeleton>
  );
};

export const StreamingBuying = () => {
  const navigate = useNavigate();
  const networkMap = useNetworkMap();
  const { basePath, path } = usePageTitleNav({
    baseURL: 'streaming/buying',
    routes: ROUTES
  });
  const company = useLocationCompany();

  const [problemCounts, setProblemCounts] = useState<ProblemCounts>();

  const hasProblems = useMemo(
    () => Object.values(problemCounts ?? {}).some(v => v > 0),
    [problemCounts]
  );

  return (
    <Page
      title={
        <BreadcrumbTitle basePath={basePath} path={path} title={PAGE_NAMES.STREAMING_BUYING} />
      }
      pageType={PAGE_NAMES.STREAMING_BUYING}
      minHeight='600px'
      minWidth='1000px'
      actions={
        path.length === 1 &&
        hasProblems &&
        problemCounts && (
          <div className='streaming-buying-problem-pane'>
            {problemCounts.unsaved > 0 && (
              <Button
                size={Size.LG}
                appearance={Appearance.EMPTY}
                design={Design.NEUTRAL}
                background={Background.DARK}
                className='problem-link'
                onClick={() => navigate(`/${company}/streaming/status`)}
              >
                Unsaved: {problemCounts.unsaved}
              </Button>
            )}
            {problemCounts.unsent > 0 && (
              <Button
                size={Size.LG}
                appearance={Appearance.EMPTY}
                design={Design.NEUTRAL}
                background={Background.DARK}
                className='problem-link'
                onClick={() => navigate(`/${company}/streaming/status`)}
              >
                Unsent: {problemCounts.unsent}
              </Button>
            )}
            {problemCounts.unconfirmed > 0 && (
              <Button
                size={Size.LG}
                appearance={Appearance.EMPTY}
                design={Design.NEUTRAL}
                background={Background.DARK}
                className='problem-link'
                onClick={() => navigate(`/${company}/streaming/status`)}
              >
                Unconfirmed: {problemCounts.unconfirmed}
              </Button>
            )}
            {problemCounts.missingTags > 0 && (
              <Button
                size={Size.LG}
                appearance={Appearance.EMPTY}
                design={Design.NEUTRAL}
                background={Background.DARK}
                className='problem-link'
                onClick={() => navigate(`/${company}/streaming/status/generate`)}
              >
                Missing Tags: {problemCounts.missingTags}
              </Button>
            )}
          </div>
        )
      }
    >
      <div className='streaming-buying'>
        {networkMap ? (
          <div className='body'>
            <Routes>
              <Route path=':network/:orderID' element={<OrderView />} />
              <Route path='*' element={<NetworkList setProblemCounts={setProblemCounts} />} />
            </Routes>
          </div>
        ) : (
          <FullPageSpinner />
        )}
      </div>
    </Page>
  );
};
